import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@vd/ui';
import { Chrome, Download, FlaskConical, Link2, Keyboard, Shield } from 'lucide-react';
import { EXTENSION_PREVIEW, type ExtensionPreview } from '../../../features/extension/extensionPreview';
import { ExtensionSetupStatus } from '../../../features/extension/ExtensionSetupStatus';
import styles from '../../McpSetupPage.module.css';

/**
 * Chrome extension setup walkthrough.
 *
 * Shown in both Company Settings → Extension and User Settings → Extension
 * so individual users can read through it without needing company admin
 * privileges, while admins can still reach it from the company nav.
 *
 * The Visual Debugger extension (MV3 Chrome) captures console / network /
 * errors / screenshots from the active tab, then submits a capture to this
 * workspace via OAuth+PKCE or a personal access token. This page
 * orients the user through install → connect → capture.
 */
/** The next version's details on this page; the banner links here with this as the hash. */
export const PREVIEW_ID = 'try-next-version';

export function ExtensionSetupTab() {
  const { hash } = useLocation();
  const [previewOpen, setPreviewOpen] = useState(hash === `#${PREVIEW_ID}`);
  useEffect(() => {
    if (hash === `#${PREVIEW_ID}`) setPreviewOpen(true);
  }, [hash]);
  return (
    <div className={styles.page}>
      <h1 className={styles.h1}>Install the extension</h1>
      <p className={styles.sub}>
        Turn any web page into a ticket, with console, network, and errors included.
      </p>

      {/* What Toado sees of the extension for this account (TOADO-973). */}
      <ExtensionSetupStatus />

      <section className={styles.step} data-tour-anchor="extension.install">
        <div className={styles.stepNum}>1</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className={styles.stepTitle}>Install</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 }}>
            One click from the Chrome Web Store. The Toado icon will appear in
            your toolbar. Pin it for one-click captures.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <a
              href="https://chromewebstore.google.com/detail/toado/lmmeeckpnachbbkilafepggbnnjhmkaj"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="primary" leftIcon={<Chrome size={14} />}>
                Install from Chrome Web Store
              </Button>
            </a>
            {EXTENSION_PREVIEW && (
              <Button
                variant="secondary"
                leftIcon={<FlaskConical size={14} />}
                aria-expanded={previewOpen}
                aria-controls={PREVIEW_ID}
                onClick={() => setPreviewOpen((open) => !open)}
              >
                {previewOpen ? 'Hide the next version' : 'Try the next version'}
              </Button>
            )}
          </div>
          {/* Most people install the store version; the next one is here for
              whoever wants it, opened in place (TOADO-797). The banner's
              "How to try it" links straight to it, open. */}
          {EXTENSION_PREVIEW && previewOpen && <PreviewSection preview={EXTENSION_PREVIEW} />}
        </div>
      </section>

      <section className={styles.step} data-tour-anchor="extension.connect">
        <div className={styles.stepNum}>2</div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Connect to your account</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 }}>
            Click the extension icon in your toolbar → <strong>Connect to Toado</strong>.
            A Chrome auth window opens to this site, asks you to approve the
            connection, and hands the extension a token. No copy-paste.
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: 10, background: 'var(--bg-muted)', borderRadius: 6,
            fontSize: 13, color: 'var(--text-secondary)',
          }}>
            <Link2 size={14} />
            Uses OAuth + PKCE. The extension never sees your password, and
            revoking its token under API tokens in your settings cuts it off.
          </div>
        </div>
      </section>

      <section className={styles.step}>
        <div className={styles.stepNum}>3</div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Capture a page</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 }}>
            On any page you want to file a bug against, click the extension
            icon. Pick a project, then <strong>Capture</strong>. The extension records:
          </p>
          <ul style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>A screenshot of the visible viewport</li>
            <li>The last 5,000 console messages</li>
            <li>All fetch / XHR requests, with headers and bodies (bounded)</li>
            <li>Uncaught errors + unhandled promise rejections</li>
            <li>Core Web Vitals + long-task spans</li>
          </ul>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: 10, marginTop: 12, background: 'var(--bg-muted)', borderRadius: 6,
            fontSize: 13, color: 'var(--text-secondary)',
          }}>
            <Keyboard size={14} />
            Default shortcuts: <kbd>Alt+C</kbd> captures the page, <kbd>Alt+B</kbd> opens the extension.
          </div>
        </div>
      </section>

      {/* The first walkthrough (TOADO-973), as the recording works (TOADO-969's map): items are
          cut when they start, so speech before the first one is in no ticket. */}
      <section className={styles.step} data-tour-anchor="extension.first-recording">
        <div className={styles.stepNum}>4</div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Record your first walkthrough</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 }}>
            A walkthrough is one recording that becomes several tickets, one for each thing you point out.
          </p>
          <ol style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>Open a page you are allowed to test, on a site one of your projects covers. Toado&apos;s panel shows on the page; press <strong>Record</strong> there (on the small Toado pill, hover to see it). No panel? Press the Toado icon in Chrome&apos;s toolbar, then <strong>Page pins</strong>. If Record will not start, the panel says why: most often no project you are in covers this site, which whoever runs the project can add.</li>
            <li>A Toado window asks what to record and which microphone to use. For a recording of the screen, Chrome then asks which tab or window to share; a recording of your voice alone skips that. Nothing is recorded until you choose.</li>
            <li><strong>Start a ticket before you talk about it.</strong> <kbd>Alt</kbd>+click the thing on the page: that pins it and starts its ticket. What you say before the first ticket is in no ticket.</li>
            <li>For something with nothing to point at, press <strong>No pin ticket</strong> (or <kbd>Alt+N</kbd>) and talk about it: that starts a second, separate ticket.</li>
            <li>Press <strong>Stop</strong> when you are done. Toado files a ticket for each one by itself once the recording is processed, usually within a few minutes, on the board of the project you recorded for. Recordings lists the walkthrough meanwhile.</li>
          </ol>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 6 }}>
            The recording window shows these steps again while you record.
          </p>
        </div>
      </section>

      <section className={styles.step}>
        <div className={styles.stepNum}>
          <Shield size={14} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Privacy &amp; redaction</h3>
          <ul style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>
              Always-redacted: <code>Authorization</code>, <code>Cookie</code>, <code>Set-Cookie</code>,{' '}
              <code>X-Api-Key</code>. URL params like <code>token</code>, <code>password</code>, <code>api_key</code> too.
            </li>
            <li>
              Per-origin <strong>Capture profiles</strong> (in Company settings) can
              block captures on sensitive origins entirely, or strip response
              bodies, or add to the redaction deny-list.
            </li>
            <li>
              Redaction runs <em>twice</em>: once in the extension before
              upload, and once server-side on ingestion.
            </li>
          </ul>
        </div>
      </section>

      <section className={styles.step} data-tour-anchor="extension.troubleshooting">
        <div className={styles.stepNum}>?</div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Troubleshooting</h3>
          <ul style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>
              <strong>"Capture not supported on this page"</strong>: the
              extension can't run on <code>chrome://</code> or the Web Store.
            </li>
            <li>
              <strong>Popup says "Not connected"</strong>: click the popup's
              Connect button. Tokens from{' '}
              <a href="/me/settings/api-tokens" style={{ color: 'var(--hl-blue)' }}>API tokens</a>{' '}
              are for AI tools, and the extension refuses them.
            </li>
            <li>
              <strong>401 on upload</strong>: the access token expired. The
              extension auto-refreshes, but if the refresh token has been
              revoked you'll need to reconnect.
            </li>
            <li>
              <strong>Console / network looks empty</strong>: captures only
              see events that happened <em>after</em> the content script ran.
              Reload the page with the extension installed, interact, then
              capture.
            </li>
          </ul>
        </div>
      </section>

    </div>
  );
}

const body: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 };

/**
 * A newer extension to load by hand while the Chrome Web Store reviews it
 * (`EXTENSION_PREVIEW`). The store build keeps working meanwhile, and only
 * one of the two can be installed, since the preview carries the store's ID.
 */
function PreviewSection({ preview }: { preview: ExtensionPreview }) {
  return (
    <section id={PREVIEW_ID} aria-labelledby="extension-preview-title"
      style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div>
        <h3 className={styles.stepTitle} id="extension-preview-title">Try version {preview.version} before the Chrome Web Store has it</h3>
        <p style={body}>
          Version {preview.version} records walkthroughs, with pins you drop on the page as you talk. Google is still
          reviewing it, so it is not on the Chrome Web Store yet. You can load it by hand now; the store
          version keeps working if you would rather wait.
        </p>
        <ol style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 20, lineHeight: 1.7, margin: '0 0 12px' }}>
          <li>
            Download it and unzip it into a folder you will keep. Chrome runs it from that folder, so
            moving or deleting the folder removes the extension.
          </li>
          <li>
            Type <code>chrome://extensions</code> into the address bar and remove Toado. Only one copy can be
            installed at a time.
          </li>
          <li>
            Turn on <strong>Developer mode</strong> at the top right, choose <strong>Load unpacked</strong>, and pick
            the folder.
          </li>
          <li>
            Pin Toado to the toolbar, click it, and choose <strong>Connect to Toado</strong>. Removing the store
            version signed you out of it.
          </li>
        </ol>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <a href={preview.href} download>
            <Button variant="primary" leftIcon={<Download size={14} />}>
              Download {preview.version} (.zip)
            </Button>
          </a>
        </div>
        <p style={{ ...body, fontSize: 13, margin: 0 }}>
          Chrome does not update an extension loaded this way. When {preview.version} reaches the Chrome Web Store,
          remove this copy from <code>chrome://extensions</code> and install Toado from the store again. Do the
          same to go back to the store version sooner.
        </p>
      </div>
    </section>
  );
}
